import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {Option, OptionGroup} from '../../../shared/models/option';
import {PopoverMenuComponent} from '../popover-menu/popover-menu.component';
import {ClickOutsideDirective} from '../../directives/click-outside.directive';
import {DayPeriod} from '../../../shared/models/time';

type Segment = 'hour' | 'minute' | 'second' | 'period';

@Component({
  selector: 'motn-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PopoverMenuComponent, ClickOutsideDirective],
})
export class TimePickerComponent implements OnInit, OnChanges {
  @Input() hour: number = 12;
  @Input() minute: number = 0;
  @Input() second: number = 0;
  @Input() period: DayPeriod = DayPeriod.AM;
  @Input() showSeconds: boolean = false;
  @Input() minuteStep: number = 1;
  @Input() secondStep: number = 1;

  @Output() hourChange = new EventEmitter<number>();
  @Output() minuteChange = new EventEmitter<number>();
  @Output() secondChange = new EventEmitter<number>();
  @Output() periodChange = new EventEmitter<DayPeriod>();

  activeSegment: Segment | null = null;

  hourOptions: OptionGroup;
  minuteOptions: OptionGroup;
  secondOptions: OptionGroup;
  periodOptions: OptionGroup;

  readonly overlayPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'center',
    overlayX: 'center',
    overlayY: 'center',
  }];

  ngOnInit(): void {
    this.buildOptions();
    this.updateSelections();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['minuteStep'] || changes['secondStep']) {
      this.buildOptions();
    }
    this.updateSelections();
  }

  openSegment(segment: Segment): void {
    this.activeSegment = segment;
  }

  private buildOptions(): void {
    const hours: Option[] = [];
    for (let h = 1; h <= 12; h++) {
      hours.push({ id: String(h), name: String(h) });
    }
    this.hourOptions = new OptionGroup(hours);
    this.minuteOptions = this.buildUnitOptions(this.minuteStep);
    this.secondOptions = this.buildUnitOptions(this.secondStep);
    this.periodOptions = new OptionGroup([
      { id: DayPeriod.AM, name: DayPeriod.AM },
      { id: DayPeriod.PM, name: DayPeriod.PM },
    ]);
  }

  private buildUnitOptions(step: number): OptionGroup {
    const options: Option[] = [];
    for (let i = 0; i < 60; i += step) {
      options.push({ id: String(i), name: String(i).padStart(2, '0') });
    }
    return new OptionGroup(options);
  }

  private updateSelections(): void {
    this.hourOptions?.changeSelected(String(this.hour));
    this.minuteOptions?.changeSelected(String(this.minute));
    this.secondOptions?.changeSelected(String(this.second));
    this.periodOptions?.changeSelected(this.period);
  }

  onHourChange(option: Option): void {
    this.hour = Number(option.id);
    this.activeSegment = null;
    this.hourChange.emit(this.hour);
  }

  onMinuteChange(option: Option): void {
    this.minute = Number(option.id);
    this.activeSegment = null;
    this.minuteChange.emit(this.minute);
  }

  onSecondChange(option: Option): void {
    this.second = Number(option.id);
    this.activeSegment = null;
    this.secondChange.emit(this.second);
  }

  onPeriodChange(option: Option): void {
    this.period = option.id as DayPeriod;
    this.activeSegment = null;
    this.periodChange.emit(this.period);
  }

  get minuteDisplay(): string {
    return String(this.minute).padStart(2, '0');
  }

  get secondDisplay(): string {
    return String(this.second).padStart(2, '0');
  }
}
